// @flow
import React, { useEffect, useRef } from 'react';
import { DataGridPro, useGridApiRef } from '@mui/x-data-grid-pro';
import Stack from '@mui/material/Stack';
import Box from '@mui/material/Box';

import style, { SCROLLED_X_ATTR } from './style';

import { type DataGridProps } from './types';

const defaultProps = {
  loading: false,
  noRowsMessage: 'No rows',
  rowSelection: false,
};

const DEFAULT_AUTOSIZE_OPTIONS = {
  includeHeaders: true,
  includeOutliers: true,
};

const DataGrid = ({
  // required props
  columns,
  rows,
  apiRef,
  loading,
  // noRowsMessage - CUSTOM message to display when there is no rows
  noRowsMessage,
  onRowClick,
  rowCount,
  slotProps,
  slots,
  localeText,
  getDetailPanelContent,
  getDetailPanelHeight,
  detailPanelExpandedRowIds,
  onDetailPanelExpandedRowIdsChange,
  rowSelection,
  pagination = false,
  autosize = false,
  autosizeOptions,
  /** Background color of each section's header. */
  headerBackgroundColor,
  /** Background color applied to the table body */
  tableBackgroundColor,
  // muiDataGridProps - CUSTOM collects all props sent in not part of the
  // wrapper and spreads them into data grid
  ...muiDataGridProps
}: DataGridProps) => {
  const internalApiRef = useGridApiRef();
  const resolvedApiRef = apiRef ?? internalApiRef;
  // Tracks whether this instance has completed its initial mount.
  const isMountedRef = useRef(false);

  const mergedAutosizeOptions = {
    ...DEFAULT_AUTOSIZE_OPTIONS,
    ...autosizeOptions,
  };

  useEffect(() => {
    if (!autosize) return;
    // Skip the first firing on mount — `autosizeOnMount` handles that via
    // MUI's internal grid lifecycle (fires after rows are rendered), which
    // avoids the race where our external effect fires before MUI has
    // committed cells to the DOM. This effect handles subsequent rows updates.
    if (!isMountedRef.current) {
      isMountedRef.current = true;
      return;
    }
    // `autosizeColumns` is async: MUI null-checks the grid root up front, but its
    // internal `extractColumnWidths` re-reads `rootElementRef.current` after an
    // `await` without guarding it. If the grid detaches mid-autosize — a StrictMode
    // remount, a section or filter swap — the resumed promise reads a null root and
    // rejects with `Cannot read properties of null (reading 'classList')`. Swallow
    // that post-unmount rejection; sizing a detached grid is a no-op anyway.
    resolvedApiRef.current
      ?.autosizeColumns(mergedAutosizeOptions)
      ?.catch(() => {});
  }, [rows, autosize]);

  // Toggle the flag imperatively on the DOM node (not React state) so the
  // pinned-column shadow tracks scroll without re-rendering the grid each frame.
  useEffect(() => {
    const api = resolvedApiRef.current;
    if (!api?.subscribeEvent) return undefined;

    return api.subscribeEvent('scrollPositionChange', ({ left }) => {
      api.rootElementRef?.current?.toggleAttribute(SCROLLED_X_ATTR, left > 0);
    });
  }, [resolvedApiRef]);

  // @ts-ignore
  return (
    <DataGridPro
      rows={rows}
      rowCount={rowCount}
      columns={columns}
      rowSelection={rowSelection}
      apiRef={resolvedApiRef}
      slots={{
        noRowsOverlay: () => (
          <Stack sx={style.noRowsMessage}>
            <Box sx={{ mt: 1 }}>{noRowsMessage}</Box>
          </Stack>
        ),
        ...slots,
      }}
      slotProps={slotProps}
      localeText={localeText}
      getDetailPanelContent={getDetailPanelContent}
      getDetailPanelHeight={getDetailPanelHeight}
      detailPanelExpandedRowIds={detailPanelExpandedRowIds}
      onDetailPanelExpandedRowIdsChange={onDetailPanelExpandedRowIdsChange}
      loading={loading}
      hideFooter={!pagination}
      pagination={pagination}
      onRowClick={onRowClick}
      disableColumnFilter
      disableColumnResize
      disableColumnSelector
      disableColumnSorting
      disableDensitySelector
      disableChildrenSorting
      disableColumnMenu
      isCellEditable={() => false}
      disableColumnReorder
      disableMultipleColumnsSorting
      disableMultipleColumnsFiltering
      {...muiDataGridProps}
      autosizeOnMount={autosize}
      autosizeOptions={mergedAutosizeOptions}
      // @ts-ignore
      sx={{
        ...style.default,
        ...muiDataGridProps.sx,
        ...(headerBackgroundColor && {
          '& .MuiDataGrid-columnHeaders, & .MuiDataGrid-columnHeader--pinnedLeft':
            {
              backgroundColor: headerBackgroundColor,
            },
          '& .MuiDataGrid-container--top [role=row]': {
            backgroundColor: headerBackgroundColor,
          },
        }),
        ...(tableBackgroundColor && {
          '& .MuiDataGrid-virtualScroller': {
            backgroundColor: tableBackgroundColor,
          },
          '& .MuiDataGrid-row': { backgroundColor: tableBackgroundColor },
        }),
      }}
    />
  );
};

DataGrid.defaultProps = defaultProps;
export default DataGrid;
